import { type ErrorLike } from '@apollo/client';
import { useApolloClient, useQuery } from '@apollo/client/react';
import { lazy, Suspense, useCallback } from 'react';
import { useCreateChatClient } from 'stream-chat-react';

import { useAccountInfo } from 'src/components/AccountInfoProvider';
import { getStreamUserId } from 'src/components/v2/LiveChat/liveChat.business';
import { LiveChatContextValue } from 'src/components/v2/LiveChat/LiveChatContext/liveChatContext';
import { LiveChatContextProvider } from 'src/components/v2/LiveChat/LiveChatContext/LiveChatContextProvider';
import { CONFIG } from 'src/config';

import type { LiveChat } from './liveChat.types';
import { EXTRANET_CHAT, LIVE_CHAT_CHANNEL_TRAINING_SESSIONS } from './queries';

const GET_STREAM_APP_KEY = CONFIG.getStreamApiKey;

const LiveChatButton = lazy(() => import('./LiveChatButton/LiveChatButton.lazy'));

export function LiveChatInitializer() {
  const { academyHash } = useAccountInfo();
  const userId = getStreamUserId(academyHash);

  const { data } = useLiveChatQuery({ skip: !userId });
  const token = data?.token;

  if (!data || !token || !userId) return null;

  if (data.channelTrainingSessions.length === 0) return null;

  const chat: LiveChat & { token: string } = {
    ...data,
    token,
  };
  return <LiveChatClient apiKey={GET_STREAM_APP_KEY} userId={userId} chat={chat} />;
}

function LiveChatClient({ apiKey, userId, chat }: { apiKey: string; userId: string; chat: LiveChat & { token: string } }) {
  const apollo = useApolloClient();

  // Refetch a fresh token on demand so the WebSocket can reconnect when the
  // JWT expires, instead of dying with the static initial token.
  const tokenProvider = useCallback(async () => {
    const { data } = await apollo.query({ query: EXTRANET_CHAT, fetchPolicy: 'network-only' });
    const refreshedToken = data?.liveChat?.token;
    if (!refreshedToken) {
      throw new Error('LiveChat: missing token from liveChat query.');
    }
    return refreshedToken;
  }, [apollo]);

  const client = useCreateChatClient({
    apiKey,
    tokenOrProvider: tokenProvider,
    options: {
      timeout: 6000,
    },
    userData: {
      id: userId,
    },
  });
  if (!client) return null;

  const contextValue: LiveChatContextValue = {
    providerUserId: userId,
    token: chat.token,
    initialUserSetting: chat.userSetting,
    channelTrainingSessions: chat.channelTrainingSessions,
    client,
  };

  return (
    <LiveChatContextProvider value={contextValue}>
      <Suspense>
        <LiveChatButton />
      </Suspense>
    </LiveChatContextProvider>
  );
}

function useLiveChatQuery({
  skip,
}: {
  skip?: boolean;
}):
  | { loading: true; error: null; data: null }
  | { loading: false; error: null; data: LiveChat }
  | { loading: false; error: ErrorLike; data: null } {
  const { data: extranetChatData, error: extranetChatError, loading: extranetChatLoading } = useQuery(EXTRANET_CHAT, { skip });
  const {
    data: liveChatChannelTrainingSessionsData,
    error: liveChatChannelTrainingSessionsError,
    loading: liveChatChannelTrainingSessionsLoading,
  } = useQuery(LIVE_CHAT_CHANNEL_TRAINING_SESSIONS, { skip });

  const liveChat = extranetChatData?.liveChat;
  const channelTrainingSessions = liveChatChannelTrainingSessionsData?.liveChatChannelTrainingSessions.channelTrainingSessions;

  if (extranetChatError)
    return {
      loading: false,
      error: extranetChatError,
      data: null,
    };

  if (liveChatChannelTrainingSessionsError)
    return {
      loading: false,
      error: liveChatChannelTrainingSessionsError,
      data: null,
    };

  if (extranetChatLoading || !liveChat)
    return {
      loading: true,
      error: null,
      data: null,
    };

  if (liveChatChannelTrainingSessionsLoading || !channelTrainingSessions)
    return {
      loading: true,
      error: null,
      data: null,
    };

  const chat: LiveChat = {
    ...liveChat,
    channelTrainingSessions: channelTrainingSessions ?? [],
  };

  return {
    loading: false,
    error: null,
    data: chat,
  };
}
